import React, { useState } from 'react';
import { Modal, Row, Col, Badge, Button, Table } from 'react-bootstrap';
import { GoogleMap, MarkerF } from '@react-google-maps/api';
import { apiClient } from '../api';
import { File as FilePdf } from 'lucide-react';

const mapContainerStyle = {
    width: '100%',
    height: '250px',
    borderRadius: '8px'
};

const formatWaitingMinutes = (minutes) => {
    if (!minutes) return "0 Min";
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hrs > 0) {
        return mins > 0 ? `${hrs} Hrs ${mins} Min` : `${hrs} Hrs`;
    }
    return `${mins} Min`;
};

const TripDetailsAccordion = ({ show, onHide, trip, isLoaded, areas = [], costCenters = [] }) => {
    const [openSection, setOpenSection] = useState("0");
    const [showVoucherConfirm, setShowVoucherConfirm] = useState(false);

    const handleClose = () => {
        setShowVoucherConfirm(false);
        onHide();
    };

    const handleDownloadVoucher = (isPaid) => {
        setShowVoucherConfirm(false);
        const url = `${apiClient.defaults.baseURL}/transport/trips/${trip.id}/voucher/pdf?is_paid=${isPaid}`;
        window.open(url, '_blank');
    };

    if (!trip) return null;

    const formatCurrency = (val, currency = 'CLP') => {
        if (!val) return currency === 'PEN' ? 'S/. 0' : (currency === 'USD' ? 'US$ 0' : '$0');
        const currUpper = String(currency || 'CLP').toUpperCase();
        if (currUpper === 'PEN') {
            return `S/. ${Math.round(val).toLocaleString()}`;
        } else if (currUpper === 'USD') {
            return `US$ ${Math.round(val).toLocaleString()}`;
        }
        return `$${Math.round(val).toLocaleString()}`;
    };
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString('es-CL');
    };

    const mapCenter = {
        lat: Number(trip.pickup_lat) || -33.4489,
        lng: Number(trip.pickup_lng) || -70.6693
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
            <Modal.Header closeButton className="bg-light">
                <Modal.Title className="h5">
                    Información del servicio <Badge bg="secondary">#{trip.id}</Badge>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-0">
                {/* Banners de Estado Administrativo */}
                {(trip.settlement_id || trip.client_invoice_id) && (
                    <div className="p-2 px-3 bg-light border-bottom d-flex align-items-center gap-2 flex-wrap">
                        <span className="small fw-bold text-muted">Estado Adm:</span>
                        {trip.settlement_id && (
                            <Badge bg="warning" text="dark">
                                🧾 Liquidación #LIQ-{String(trip.settlement_id).padStart(5, '0')}
                            </Badge>
                        )}
                        {trip.client_invoice && (() => {
                            const inv = trip.client_invoice;
                            const isPaid = inv.status === 'paid';
                            const invoiceDocs = inv.documents?.filter(doc => doc.type === 'FACTURA') || [];
                            const folios = invoiceDocs.map(doc => doc.folio).join(', ');
                            
                            return (
                                <>
                                    <Badge bg={isPaid ? "success" : "info"}>
                                        {isPaid ? `✅ Prefactura #${inv.internal_number || inv.id} (Pagada)` : `📄 Prefactura #${inv.internal_number || inv.id}`}
                                    </Badge>
                                    {invoiceDocs.length > 0 && (
                                        <Badge bg="secondary">
                                            💰 {invoiceDocs.length === 1 ? 'Factura' : 'Facturas'} #{folios}
                                        </Badge>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                )}
                <div className="p-3 bg-white border-bottom">
                    <Row className="mb-2">
                        <Col xs={6} md={3}>
                            <small className="text-muted d-block">Solicitud N°</small>
                            <strong>{trip.id}</strong>
                        </Col>
                        <Col xs={6} md={3}>
                            <small className="text-muted d-block">Estado</small>
                            <Badge bg="info">{trip.status}</Badge>
                        </Col>
                        <Col xs={6} md={3}>
                            <small className="text-muted d-block">Ingresado por</small>
                            <span>
                                {trip.trip_source === 'api' ? 'API BOOKING' :
                                 trip.trip_source === 'web' ? 'PORTAL WEB' :
                                 trip.trip_source === 'import' ? 'IMPORTACIÓN EXCEL' :
                                 'MANUAL APP'}
                            </span>
                        </Col>
                        <Col xs={6} md={3}>
                            <small className="text-muted d-block">Fecha Inicio</small>
                            <span>{formatDate(trip.scheduled_time)}</span>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col xs={6} md={3}>
                            <small className="text-muted d-block">Cliente</small>
                            <strong>{trip.client?.name || '---'}</strong>
                        </Col>
                        <Col xs={6} md={3}>
                            <small className="text-muted d-block">Solicitante</small>
                            <strong>{trip.external_solicitante || trip.requester?.full_name || '---'}</strong>
                        </Col>
                        <Col xs={6} md={3}>
                            <small className="text-muted d-block">Pasajero</small>
                            <span>{trip.passenger_name}</span>
                        </Col>
                        <Col xs={6} md={3}>
                            {/* Espacio libre */}
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={6} md={3}>
                            <small className="text-muted d-block">Vehículo</small>
                            <span>{trip.requested_vehicle_type?.name || 'Standard'}</span>
                        </Col>
                        <Col xs={6} md={3}>
                            <small className="text-muted d-block">Móvil Asignado</small>
                            <strong>{trip.vehicle?.internal_code || '---'}</strong>
                        </Col>
                        <Col xs={6} md={3}>
                            <small className="text-muted d-block">Proveedor Sugerido</small>
                            <strong className="text-primary">{trip.suggested_provider?.name || '---'}</strong>
                        </Col>
                        <Col xs={6} md={3}>
                            <small className="text-muted d-block">Proveedor Asignado</small>
                            <strong>{trip.provider?.name || '---'}</strong>
                        </Col>
                    </Row>
                </div>

                {/* Secciones de Detalle con control manual robusto */}
                <div className="border-top">
                    {/* 1. Direcciones y Mapa */}
                    <div className="border-bottom">
                        <div 
                            className="p-3 d-flex justify-content-between align-items-center bg-light"
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => setOpenSection(openSection === "0" ? null : "0")}
                        >
                            <strong className="text-slate-700">📍 Direcciones y Mapa</strong>
                            <span className="text-muted small">{openSection === "0" ? '▲' : '▼'}</span>
                        </div>
                        {openSection === "0" && (() => {
                            try {
                                return (
                                    <div className="p-3 bg-white border-top">
                                        <div className="mb-3">
                                            <div className="mb-2">
                                                <strong className="text-success">A (Origen):</strong> {trip.pickup_address}
                                            </div>
                                            {trip.stops?.map((stop, idx) => (
                                                <div key={idx} className="mb-2 ps-3 border-start">
                                                    <strong>P{idx+1}:</strong> {stop.address}
                                                </div>
                                            ))}
                                            <div>
                                                <strong className="text-danger">B (Destino):</strong> {trip.dropoff_address}
                                            </div>
                                        </div>
                                        {isLoaded && (
                                            <GoogleMap
                                                mapContainerStyle={mapContainerStyle}
                                                center={mapCenter}
                                                zoom={12}
                                                options={{ disableDefaultUI: true }}
                                            >
                                                {trip.pickup_lat && <MarkerF position={{ lat: Number(trip.pickup_lat), lng: Number(trip.pickup_lng) }} label="A" />}
                                                {trip.dropoff_lat && <MarkerF position={{ lat: Number(trip.dropoff_lat), lng: Number(trip.dropoff_lng) }} label="B" />}
                                            </GoogleMap>
                                        )}
                                    </div>
                                );
                            } catch (e) {
                                return <div className="alert alert-danger m-3 small">⚠️ Error al cargar mapa: {e.message}</div>;
                            }
                        })()}
                    </div>

                    {/* 2. Información Financiera */}
                    <div className="border-bottom">
                        <div 
                            className="p-3 d-flex justify-content-between align-items-center bg-light"
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => setOpenSection(openSection === "1" ? null : "1")}
                        >
                            <strong className="text-slate-700">💰 Información Financiera</strong>
                            <span className="text-muted small">{openSection === "1" ? '▲' : '▼'}</span>
                        </div>
                        {openSection === "1" && (() => {
                            try {
                                const resolvedCC = trip.cost_center || (costCenters || []).find(c => String(c.id) === String(trip.cost_center_id)) || trip.requester?.cost_center;
                                const ccCode = resolvedCC?.code ? String(resolvedCC.code).trim() : '';
                                const ccName = resolvedCC?.name ? String(resolvedCC.name).trim() : '';
                                const formattedCC = (ccCode && ccName && ccCode !== ccName)
                                    ? `${ccCode} - ${ccName}`
                                    : (ccCode || ccName || trip.cost_center_label || '---');

                                const resolvedArea = trip.area || (areas || []).find(a => String(a.id) === String(trip.area_id)) || trip.requester?.area;
                                const formattedArea = resolvedArea?.name || trip.area_label || '---';

                                const formattedOS = trip.external_reference || trip.client_file_number || '---';
                                const formattedFile = trip.file || resolvedCC?.file || trip.client_file_number || '---';

                                return (
                                    <div className="p-3 bg-white border-top">
                                        <Table size="sm" borderless className="mb-0">
                                            <tbody>
                                                <tr><td>Centro de Costo:</td><td><strong>{formattedCC}</strong></td></tr>
                                                <tr><td>Área:</td><td>{formattedArea}</td></tr>
                                                <tr><td>Naturaleza Gasto:</td><td>{trip.expense_nature || '---'}</td></tr>
                                                <tr><td>File / OS:</td><td>{formattedOS}</td></tr>
                                                <tr><td>File:</td><td>{formattedFile}</td></tr>
                                                {trip.oc_number && (
                                                    <tr><td>Orden de Compra (OC):</td><td>{trip.oc_number}</td></tr>
                                                )}
                                                {trip.aquivoy_id && (
                                                    <tr><td>Solicitud AQUIVOY:</td><td>{trip.aquivoy_id}</td></tr>
                                                )}
                                                <tr className="border-top text-primary">
                                                    <td><strong>Venta Base:</strong></td>
                                                    <td><strong>{formatCurrency(trip.estimated_price, trip.local_currency_code)}</strong></td>
                                                </tr>
                                                {trip.waiting_time_minutes > 0 && (
                                                    <tr className="text-warning">
                                                        <td>Espera ({formatWaitingMinutes(trip.waiting_time_minutes)}):</td>
                                                        <td><strong>+{formatCurrency(trip.waiting_time_amount, trip.local_currency_code)}</strong></td>
                                                    </tr>
                                                )}
                                                {(trip.extra_services || []).map((s, idx) => (
                                                    <tr key={idx} className="text-info">
                                                        <td>{s.name || s.id}:</td>
                                                        <td><strong>+{formatCurrency(s.price || 0, trip.local_currency_code)}</strong></td>
                                                    </tr>
                                                ))}
                                                <tr className="border-top bg-light">
                                                    <td><strong>TOTAL VENTA:</strong></td>
                                                    <td><strong>{formatCurrency(
                                                        (trip.estimated_price || 0) + 
                                                        (trip.waiting_time_amount || 0) + 
                                                        (trip.extra_services || []).reduce((sum, s) => sum + (parseFloat(s.price) || 0), 0),
                                                        trip.local_currency_code
                                                    )}</strong></td>
                                                </tr>
                                                <tr>
                                                    <td>Costo Base:</td>
                                                    <td>
                                                        {formatCurrency(
                                                            (trip.provider_currency_code || trip.local_currency_code || 'CLP') !== 'CLP' 
                                                                ? trip.provider_cost_local 
                                                                : trip.provider_cost_base, 
                                                            trip.provider_currency_code || trip.local_currency_code
                                                        )}
                                                    </td>
                                                </tr>
                                                {trip.waiting_time_provider_cost > 0 && (
                                                    <tr>
                                                        <td>Costo Espera:</td>
                                                        <td>{formatCurrency(trip.waiting_time_provider_cost, trip.provider_currency_code || trip.local_currency_code)}</td>
                                                    </tr>
                                                )}
                                                <tr className="border-top">
                                                    <td><strong>Pago Conductor:</strong></td>
                                                    <td className="text-success fw-bold">
                                                        {formatCurrency(
                                                            (trip.driver_earnings || 0) + (trip.waiting_time_provider_cost || 0), 
                                                            trip.provider_currency_code || trip.local_currency_code
                                                        )}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </Table>
                                    </div>
                                );
                            } catch (e) {
                                return <div className="alert alert-danger m-3 small">⚠️ Error al cargar info financiera: {e.message}</div>;
                            }
                        })()}
                    </div>

                    {/* 3. Pasajeros */}
                    <div className="border-bottom">
                        <div 
                            className="p-3 d-flex justify-content-between align-items-center bg-light"
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => setOpenSection(openSection === "2" ? null : "2")}
                        >
                            <strong className="text-slate-700">👥 Información Pasajeros</strong>
                            <span className="text-muted small">{openSection === "2" ? '▲' : '▼'}</span>
                        </div>
                        {openSection === "2" && (() => {
                            try {
                                return (
                                    <div className="p-3 bg-white border-top">
                                        <ul className="list-unstyled mb-0">
                                            <li className="mb-2">
                                                <strong>Principal:</strong> {trip.passenger_name || '---'} <br/>
                                                <small className="text-muted">{trip.passenger_phone || 'Sin teléfono'}</small>
                                            </li>
                                            {(trip.additional_passengers || []).map((p, pidx) => (
                                                <li key={p.id || pidx} className="mb-2 border-top pt-2">
                                                    <strong>Acompañante:</strong> {p.full_name || '---'} <br/>
                                                    <small className="text-muted">{p.phone || 'Sin teléfono'}</small>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                );
                            } catch (e) {
                                return <div className="alert alert-danger m-3 small">⚠️ Error al cargar pasajeros: {e.message}</div>;
                            }
                        })()}
                    </div>

                    {/* 4. Conductor y Vehículo */}
                    <div className="border-bottom">
                        <div 
                            className="p-3 d-flex justify-content-between align-items-center bg-light"
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => setOpenSection(openSection === "3" ? null : "3")}
                        >
                            <strong className="text-slate-700">🚘 Información Conductor</strong>
                            <span className="text-muted small">{openSection === "3" ? '▲' : '▼'}</span>
                        </div>
                        {openSection === "3" && (() => {
                            try {
                                return (
                                    <div className="p-3 bg-white border-top">
                                        <Row>
                                            <Col md={6} className="mb-3 mb-md-0">
                                                <h6>Conductor</h6>
                                                <div>{trip.driver?.full_name || 'Sin asignar'}</div>
                                                <div className="text-muted small">{trip.driver?.rut}</div>
                                                <div className="text-muted small">{trip.driver?.phone}</div>
                                            </Col>
                                            <Col md={6}>
                                                <h6>Vehículo</h6>
                                                <div><strong>Móvil: {trip.vehicle?.internal_code || '---'}</strong></div>
                                                <div>{trip.vehicle?.make} {trip.vehicle?.model}</div>
                                                <div>Patente: {trip.vehicle?.plate}</div>
                                                <div className="text-muted small">{trip.vehicle?.color}</div>
                                            </Col>
                                        </Row>
                                    </div>
                                );
                            } catch (e) {
                                return <div className="alert alert-danger m-3 small">⚠️ Error al cargar info de conductor: {e.message}</div>;
                            }
                        })()}
                    </div>

                    {/* 5. Notas */}
                    <div className="border-bottom">
                        <div 
                            className="p-3 d-flex justify-content-between align-items-center bg-light"
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => setOpenSection(openSection === "4" ? null : "4")}
                        >
                            <strong className="text-slate-700">📝 Observaciones</strong>
                            <span className="text-muted small">{openSection === "4" ? '▲' : '▼'}</span>
                        </div>
                        {openSection === "4" && (() => {
                            try {
                                return (
                                    <div className="p-3 bg-white border-top">
                                        <p className="mb-2"><strong>Generales:</strong> {trip.notes || '---'}</p>
                                        <p className="mb-0"><strong>Del Conductor:</strong> {trip.driver_notes || '---'}</p>
                                    </div>
                                );
                            } catch (e) {
                                return <div className="alert alert-danger m-3 small">⚠️ Error al cargar observaciones: {e.message}</div>;
                            }
                        })()}
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between align-items-center">
                {showVoucherConfirm ? (
                    <div className="d-flex align-items-center gap-2">
                        <span className="small text-muted me-2">¿Timbre de agua?</span>
                        <Button 
                            variant="success" 
                            size="sm" 
                            onClick={() => handleDownloadVoucher(true)}
                            className="fw-bold"
                        >
                            💰 PAGADO
                        </Button>
                        <Button 
                            variant="warning" 
                            size="sm" 
                            className="text-dark fw-bold"
                            onClick={() => handleDownloadVoucher(false)}
                        >
                            ⏳ PENDIENTE
                        </Button>
                        <Button 
                            variant="link" 
                            size="sm" 
                            className="text-muted text-decoration-none" 
                            onClick={() => setShowVoucherConfirm(false)}
                        >
                            Cancelar
                        </Button>
                    </div>
                ) : (
                    <Button 
                        variant="outline-danger" 
                        onClick={() => setShowVoucherConfirm(true)}
                        className="d-flex align-items-center gap-1 shadow-sm"
                    >
                        <FilePdf size={16} /> Generar Voucher
                    </Button>
                )}
                <Button variant="secondary" onClick={handleClose}>Cerrar</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default TripDetailsAccordion;
